import {
  useQueries,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import axios from "@lhd/libs/axiosV2";
import { AxiosRequestConfig } from "axios";
import { setMockData, deletedMockData, getMockData } from "../mockAPIs/worker";
import { TableKey } from "@lhd/constants";
import { getCompanyId } from "@lhd/utility";
// Type definitions
export interface QueryConfig {
  queryKey: (string | number)[];
  url: string;
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  params?: Record<string, any>;
  data?: any;
  headers?: Record<string, string>;
  enabled?: boolean;
  staleTime?: number;
  cacheTime?: number;
  retry?: number | boolean;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean | "always";
  // New properties for smart pagination
  pageDependent?: boolean; // Whether this query depends on page number
  pageRange?: number[]; // Specific pages this query should run on
  condition?: (params: any) => boolean; // Custom condition function
  // Individual pagination for each table
  pagination?: {
    currentPage: number;
    pageSize: number;
    totalPages?: number;
  };
}

export interface QueryAllConfig {
  queries: QueryConfig[];
  options?: {
    enabled?: boolean;
    staleTime?: number;
    cacheTime?: number;
    retry?: number | boolean;
    refetchOnWindowFocus?: boolean;
    refetchOnMount?: boolean | "always";
  };
  // Pagination context
  pagination?: {
    currentPage: number;
    pageSize: number;
    totalPages?: number;
  };
  // Additional context for conditions
  context?: Record<string, any>;
}

export interface QueryAllResult<T = any> {
  data: Record<string, T>;
  isLoading: Record<string, boolean>;
  isError: boolean;
  errors: Error[];
  refetch: () => void;
  results: UseQueryResult<T>[];
  // Pagination info
  paginationInfo?: Record<
    string,
    {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    }
  >;
  resultsObj: Record<string, UseQueryResult>;
}

/**
 * Smart hook to query multiple APIs with intelligent pagination
 * Only calls APIs that are needed for the current page/context
 */
export const useQueryAll = <T = any>(
  config: QueryAllConfig,
): QueryAllResult<T> => {
  const { queries, options = {}, pagination, context = {} } = config;

  // Filter queries based on pagination and conditions
  const activeQueries = queries.filter((query) => {
    // If query is disabled, skip it
    if (query.enabled === false) return false;

    // If query is page dependent, check page range and pageSize
    if (query.pageDependent && query.pagination) {
      const { currentPage, pageSize } = query.pagination;

      // If pageRange is specified, check if current page is in range
      if (query.pageRange && !query.pageRange.includes(currentPage)) {
        return false;
      }

      // If no pageRange specified, run on all pages
      // pageDependent queries will now also depend on pageSize changes
    }

    // Check custom condition
    if (query.condition) {
      const shouldRun = query.condition({
        pagination: query.pagination || pagination,
        context,
        query,
      });
      if (!shouldRun) return false;
    }

    return true;
  });
  // Convert QueryConfig to UseQueryOptions
  const queryOptions: UseQueryOptions<any>[] = activeQueries.map((query) => {
    const axiosConfig: AxiosRequestConfig = {
      method: query.method || "GET",
      url: query.url,
      params: query.params,
      data: query.data,
      headers: query.headers,
      // baseURL: API_V1_URL,
    };
    const url = new URL(window.location.href);
    const pathParts = url.pathname.split("/");
    const id = pathParts[2];

    const queryKey =
      query.pageDependent && query.pagination
        ? [
            ...query.queryKey,
            query.pagination.currentPage,
            query.pagination.pageSize,
            getCompanyId(),
            id,
          ]
        : query.queryKey;
    return {
      queryKey,
      queryFn: () => {
        //if (keyMocks.includes(queryKey[0])) {
        // if (axiosConfig.method === "GET") {
        //   return getMockData({
        //     page: query?.params?.page || 1,
        //     pageSize: query?.params?.per_page || 5,
        //     table: queryKey[0] + "",
        //   });
        // }
        // }
        // return axios(axiosConfig).then((res) => res.data);
        return axios(axiosConfig).then((res) => ({
          data: res.data,
          pagination: Object.fromEntries(
            Object.entries(res.headers).map(([k, v]) => [k, v ?? ""]),
          ) as Record<string, string>,
        }));
      },
      enabled: query.enabled !== false && options.enabled !== false,
      staleTime: query.staleTime ?? options.staleTime ?? 5 * 60 * 1000,
      cacheTime: query.cacheTime ?? options.cacheTime ?? 10 * 60 * 1000,
      retry: query.retry ?? options.retry ?? 3,
      refetchOnWindowFocus:
        query.refetchOnWindowFocus ?? options.refetchOnWindowFocus ?? false,
      refetchOnMount: query.refetchOnMount ?? options.refetchOnMount ?? true,
    };
  });

  // Execute only active queries
  const results = useQueries({
    queries: queryOptions,
  }).map((result) => ({
    ...result,
    data: result?.data?.data || [],
    pagination: result?.data?.pagination || {},
  }));

  // Extract data và paginationInfo cho từng tableKey
  const data: Record<string, any> = {};
  const paginationInfo: Record<
    string,
    { page: number; pageSize: number; total: number; totalPages: number }
  > = {};
  results.forEach((result, idx) => {
    const queryOption = queryOptions[idx];
    if (!queryOption) return;
    const key = Array.isArray(queryOption.queryKey)
      ? queryOption.queryKey[0]
      : queryOption.queryKey;
    if (result.data) {
      data[key as string] = result.data;
      if (result.pagination) {
        const pagination = result.pagination;
        paginationInfo[key as string] = {
          page: parseInt(pagination["x-page"] || "1"),
          pageSize: parseInt(pagination["x-per-page"] || "5"),
          totalPages: parseInt(pagination["x-total-pages"] || "1"),
          total: parseInt(pagination["x-total"] || "0"), // api
        };
      }
    }
  });
  // const isLoading = results.some((result) => result.isLoading);
  const isLoading = results.reduce((acc, result, idx) => {
    const queryOption = queryOptions[idx];
    const key: string = Array.isArray(queryOption.queryKey)
      ? queryOption.queryKey[0]
      : queryOption.queryKey;
    acc[key as string] = result.isLoading;
    return acc;
  }, {} as Record<string, boolean>);
  const isError = results.some((result) => result.isError);
  const errors = results
    .map((result) => result.error)
    .filter(Boolean) as Error[];

  const resultsObj = results.reduce((acc, result, index) => {
    const queryOption = queryOptions[index];
    if (!queryOption) return acc; // skip nếu undefined

    const key = Array.isArray(queryOption.queryKey)
      ? queryOption.queryKey[0]
      : queryOption.queryKey;

    acc[key as string] = result;
    return acc;
  }, {} as Record<string, UseQueryResult>);
  // Refetch function
  const refetch = () => {
    results.forEach((result) => {
      if (result.refetch) {
        result.refetch();
      }
    });
  };

  return {
    data,
    isLoading,
    isError,
    errors,
    refetch,
    results,
    resultsObj,
    paginationInfo,
  };
};

/**
 * Utility function to create query configs for common API patterns
 */
export const createQueryConfigs = {
  // For fetching multiple resources by IDs
  byIds: <T>(
    baseUrl: string,
    ids: (string | number)[],
    additionalParams?: Record<string, any>,
  ): QueryConfig[] => {
    return ids.map((id) => ({
      queryKey: [baseUrl, id],
      url: `${baseUrl}/${id}`,
      method: "GET",
      params: additionalParams,
    }));
  },

  // For fetching different types of data
  mixed: (
    configs: Array<{ key: string; url: string; params?: Record<string, any> }>,
  ): QueryConfig[] => {
    return configs.map((config) => ({
      queryKey: [config.key],
      url: config.url,
      method: "GET",
      params: config.params,
    }));
  },

  // For paginated queries
  paginated: (
    baseUrl: string,
    pages: number[],
    pageSize: number,
    additionalParams?: Record<string, any>,
  ): QueryConfig[] => {
    return pages.map((page) => ({
      queryKey: [baseUrl, "page", page, pageSize],
      url: baseUrl,
      method: "GET",
      params: {
        page,
        per_page: pageSize,
        ...additionalParams,
      },
      pageDependent: true,
      pageRange: [page],
    }));
  },

  // For completely different APIs
  differentApis: (
    apis: Array<{
      key: string;
      url: string;
      method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
      params?: Record<string, any>;
      data?: any;
      headers?: Record<string, string>;
      pageDependent?: boolean;
      pageRange?: number[];
      condition?: (params: any) => boolean;
    }>,
  ): QueryConfig[] => {
    return apis.map((api) => ({
      queryKey: [api.key],
      url: api.url,
      method: api.method || "GET",
      params: api.params,
      data: api.data,
      headers: api.headers,
      pageDependent: api.pageDependent,
      pageRange: api.pageRange,
      condition: api.condition,
    }));
  },

  // Smart pagination - only fetch what's needed
  smartPagination: (
    configs: Array<{
      key: string;
      url: string;
      params?: Record<string, any>;
      pageDependent?: boolean;
      pageRange?: number[];
      condition?: (params: any) => boolean;
    }>,
  ): QueryConfig[] => {
    return configs.map((config) => ({
      queryKey: [config.key],
      url: config.url,
      method: "GET",
      params: config.params,
      pageDependent: config.pageDependent ?? false,
      pageRange: config.pageRange,
      condition: config.condition,
    }));
  },
};

/**
 * Example usage for smart pagination:

 * const { data, isLoading, paginationInfo } = useQueryAll({
 *   queries: createQueryConfigs.smartPagination([
 *     {
 *       key: "workers",
 *       url: "/api/workers",
 *       params: { page: currentPage, per_page: pageSize },
 *       pageDependent: true, // Only fetch when page changes
 *     },
 *     {
 *       key: "analytics",
 *       url: "/api/analytics",
 *       pageDependent: false, // Always fetch (cached)
 *     },
 *     {
 *       key: "page-specific-data",
 *       url: "/api/page-data",
 *       pageDependent: true,
 *       pageRange: [1, 3, 5], // Only fetch on pages 1, 3, 5
 *     },
 *     {
 *       key: "conditional-data",
 *       url: "/api/conditional",
 *       condition: ({ pagination }) => pagination?.currentPage > 5, // Only fetch after page 5
 *     }
 *   ]),
 *   pagination: {
 *     currentPage: 1,
 *     pageSize: 10,
 *     totalPages: 20
 *   },
 *   context: {
 *     userRole: "admin",
 *     featureFlags: { analytics: true }
 *   }
 * });
 *
 * // Example 2: Conditional queries based on user role
 * const { data, isLoading } = useQueryAll({
 *   queries: [
 *     { queryKey: ["basic-data"], url: "/api/basic" },
 *     {
 *       queryKey: ["admin-data"],
 *       url: "/api/admin",
 *       condition: ({ context }) => context.userRole === "admin"
 *     },
 *     {
 *       queryKey: ["analytics"],
 *       url: "/api/analytics",
 *       condition: ({ context }) => context.featureFlags?.analytics
 *     }
 *   ],
 *   context: { userRole: "admin", featureFlags: { analytics: true } }
 * });
 *
 * // Example 3: Page-specific data loading
 * const { data, paginationInfo } = useQueryAll({
 *   queries: [
 *     {
 *       key: "page-1-data",
 *       url: "/api/page1",
 *       pageDependent: true,
 *       pageRange: [1]
 *     },
 *     {
 *       key: "page-2-data",
 *       url: "/api/page2",
 *       pageDependent: true,
 *       pageRange: [2]
 *     },
 *     {
 *       key: "common-data",
 *       url: "/api/common",
 *       pageDependent: false // Always loaded
 *     }
 *   ],
 *   pagination: { currentPage: 1, pageSize: 10 }
 * });
 *
 */
